/**
 * Accordion-style level list within the CreateLevels editor.
 *
 * @module components/sim/createLevels/Levels
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
    Grid,
    Box,
    Paper,
    Typography,
    Button,
    TextField,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import {useLevelsStyle} from './CreateLevels.js';
import LevelOptions from "./LevelOptions";
import LevelActions from "./LevelActions";
import {addLevel} from "../../../services/result.service";
//import passFailCriteria from "./PassFailCriteria";

/**
 * Levels component representing the UI and functionality for managing various levels within a simulation.
 * It provides functionalities such as creating, updating, deleting, and navigating between different levels
 * along with rendering associated actions and options.
 *
 * @param {Object} props - Component properties.
 * @param {boolean} props.simGenerated - Flag indicating if the levels are system-generated.
 * @param {Function} props.Level - Function to update the active level data.
 * @param {Array} props.levels - List of levels.
 * @param {string} props.passFailLevel - Flag for pass/fail level functionality.
 * @param {Object} props.data - The simulation data.
 * @param {Function} props.setData - Function to update simulation data.
 * @param {Function} props.updateData - Function to update specific level data.
 * @param {boolean} props.deleted - Flag indicating if a level is deleted.
 * @param {boolean} props.flag - Flag used for controlling the update of levels.
 * @param {Function} props.setFlag - Function to update the flag value.
 * @param {Function} props.setDeleted - Function to reset the deleted status.
 * @param {Function} props.setIndex - Function to set the current active level index.
 * @param {Function} props.onOptionChange - Function called upon changing level options.
 * @param {Function} props.setAlertType - Function to set alert type.
 * @param {Function} props.setAlertMessage - Function to set alert messages.
 * @param {Function} props.setOpenAlert - Function to control the alert's visibility.
 * @param {Function} props.setLoad - Function to set the loading state during async operations.
 * @param {boolean} props.load - Flag indicating if an async operation is ongoing.
 */
const Levels = ({
    simGenerated,
    Level,
    levels,
    passFailLevel,
    data,
    setData,
    updateData,
    deleted,
    flag,
    setFlag,
    setDeleted,
    setIndex,
    onOptionChange,
    setAlertType,
    setAlertMessage,
    setOpenAlert,
    setLoad,
    load,
    setGeneratingAudio,
}) => {
    const classes = useLevelsStyle();
    const [insertLevel, setInsertLevel] = useState(false);
    const [levelName, setLevelName] = useState('');
    const [levelOrder, setLevelOrder] = React.useState(0);
    const [activeLevel, setActiveLevel] = React.useState(0);

    useEffect(() => {
        if (levels.length && flag) {
            const simData = { ...data };
            const { length } = simData.levels;
            const latestOrder = simData.levels[length - 1].order;
            setLevelOrder(latestOrder);
            setFlag(false);
        }
    }, [levels.length, flag, setLevelOrder, data, setFlag]);

    useEffect(() => {
        if (deleted) {
            setIndex(levelOrder - 1);
            setDeleted(false);
        }
    }, [deleted, setDeleted, setIndex, levelOrder]);

    /**
     * Handles the logic to set the current active level and its index based on the provided level.
     * It updates the relevant state to reflect the active level and performs an operation associated with the level.
     *
     * @param {Object} level - The provided level object to be handled.
     * @param {number} level.order - The order property of the level object that determines its sequence.
     */
    const handleTabs = (level) => {
        const index = levels.findIndex((lvl) => lvl.order === level.order);
        setIndex(index);
        setActiveLevel(index);
        Level(level);
    };

    /**
     * Asynchronous function responsible for creating and adding a new level in the system.
     *
     * The function utilizes the `addLevel` API to create a level with the specified properties, including its
     * associated simulation ID, name, order, and default configurations (e.g., `hint_text`, `keyword_score`, etc.).
     *
     * Once the level creation is successful (`result.data.success`), the new level is appended to the
     * existing simulation data, the level order is incremented, and relevant state variables are updated.
     *
     * @async
     * @function save
     * @returns {Promise<void>} Resolves when the level has been successfully added and state updates are completed.
     */
    const save = async () => {
        let result = await addLevel({
            sim_id: data.id,
            name: levelName,
            order: Math.max.apply(Math, levels.map((o) => o.order + 1)),
            hint_text: true,
            keyword_score: true,
            send_to_snowfly: false
        });
        if (result.data.success) {
            const simData = { ...data };
            simData.levels.push({...result.data.level, _id: result.data.level.id, scripts: []});
            setLevelOrder(levelOrder + 1);
            setData(simData);
        }

        setLevelName('');
        setInsertLevel(false);
    };

    /**
     * Cancels the current operation by resetting certain state properties.
     *
     * This function sets the `insertLevel` state to `false` and clears
     * the `levelName` value by setting it to an empty string.
     * It is typically used to handle cancellation actions in a UI workflow.
     */
    const cancel = () => {
        setInsertLevel(false);
        setLevelName('');
    };

    /**
     * A function that triggers the creation of a new level.
     * This function sets the `insertLevel` state to `true`, indicating that a new level should be created.
     */
    const createNewLevel = () => {
        setInsertLevel(true);
    };

    return (
        <Grid item key="card" className={classes.firstGrid}>
            <Paper elevation={0}>
                <Grid
                    container
                    spacing={2}
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                >
                    <Grid className={classes.levelHeader}>
                        <Typography className={classes.levelLabel}>
                            Levels
                            <span className={classes.simGeneratedLabel}>
                                <CheckCircleIcon className={classes.checkCircleIcon} />
                                SIM GENERATED
                            </span>
                        </Typography>
                    </Grid>
                    <Grid className={classes.levels} item xs>
                        {levels.map((level, levelIndex) => (
                            <Box
                                onClick={() => handleTabs(level)}
                                className={activeLevel === levelIndex ?
                                    classes.activeCard :
                                    classes.inactiveCard
                                }
                                key={levelIndex}
                            >
                                <Typography className={classes.label} component={'div'}>
                                    {simGenerated ? (
                                        <CheckCircleIcon className={classes.checkCircleIcon}/>
                                    ) : null}
                                    LEVEL
                                    {' '}
                                    {levelIndex + 1}
                                    :
                                    <div style={{
                                        float: "right",
                                        marginRight: "-1em",
                                        marginTop: "-1em",
                                        marginLeft: "0.5em",
                                        width: "fit-content",
                                        textAlign: "right"
                                    }}>
                                        <LevelOptions
                                            data={data}
                                            singleLevel={level}
                                            levelIndex={levelIndex}
                                            passFailLevel={passFailLevel}
                                            setData={setData}
                                            classes={classes}
                                            updateData={updateData}
                                            onOptionChange={onOptionChange}
                                            setAlertType={setAlertType}
                                            setAlertMessage={setAlertMessage}
                                            setOpenAlert={setOpenAlert}
                                            setLoad={setLoad}
                                            load={load}
                                        />
                                        <br />
                                        <LevelActions classes={classes}
                                            data={data}
                                            updateData={updateData}
                                            levelIndex={levelIndex}
                                            deleted={deleted}
                                            setDeleted={setDeleted}
                                            setData={setData}
                                            setIndex={setIndex}
                                            setLoad={setLoad}
                                            load={load}
                                            setGeneratingAudio={setGeneratingAudio}
                                        />
                                    </div>
                                </Typography>
                                <Typography className={classes.levelName}>
                                    {level.name}
                                </Typography>
                            </Box>
                        ))}
                        {insertLevel ? (
                            <Box className={classes.inactiveCard}>
                                <Typography className={classes.label}>
                                    {simGenerated ? (
                                        <CheckCircleIcon className={classes.checkCircleIcon} />
                                    ) : null}
                                    LEVEL
                                    {' '}
                                    {data.levels.length + 1}
                                    :
                                </Typography>
                                <TextField
                                    placeholder="Enter Level name"
                                    className={classes.text}
                                    value={levelName}
                                    onChange={(e) => setLevelName(e.target.value)}
                                />
                                <div className={classes.textFielDiv}>
                                    <Button
                                        name="script"
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        className={classes.saveBtn}
                                        onClick={() => save()}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="default"
                                        size="small"
                                        className={classes.cancelBtn}
                                        onClick={() => cancel()}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </Box>
                        ) : null}
                        <Typography className={classes.addLevel} onClick={createNewLevel}>
                            + ADD A LEVEL
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

Levels.propTypes = {
    simGenerated: PropTypes.bool,
    levels: PropTypes.array,
    Level: PropTypes.func,
    data: PropTypes.object,
    setData: PropTypes.func,
    levelOrder: PropTypes.number,
    setLevelOrder: PropTypes.func,
    deleted: PropTypes.bool,
    flag: PropTypes.bool,
    setFlag: PropTypes.func,
    setDeleted: PropTypes.func,
    passFailLevel: PropTypes.string,
    updateData: PropTypes.func,
    setIndex: PropTypes.func,
    onOptionChange: PropTypes.func,
    setAlertType: PropTypes.func,
    setAlertMessage: PropTypes.func,
    setOpenAlert: PropTypes.func,
    setLoad: PropTypes.func,
    load: PropTypes.bool,
    setGeneratingAudio: PropTypes.func,
};

export default Levels;
